/**
 * Shared record review client (US-061-08).
 *
 * Admin queue of shared-layer Market Intelligence records waiting for review
 * (Proposed first-batch records and Flagged contradictions), on TaskManager.API:
 *   GET  /api/TaskManager/Admin/Agent/IntelligenceReview?status=&page=&pageSize=
 *   POST /api/TaskManager/Admin/Agent/IntelligenceReview/{recordId}/Approve
 *   POST /api/TaskManager/Admin/Agent/IntelligenceReview/{recordId}/Reject   body { reason }
 * Non-admins get 403; an already-reviewed record gets 409.
 */
import { authenticatedFetch } from './authenticatedFetch';

const BASE = '/api/TaskManager/Admin/Agent/IntelligenceReview';

export type SharedRecordStatus = 'Proposed' | 'Flagged';

export interface SharedRecord {
  recordId: string;
  key: string;
  /** Business type (taxonomy node name). */
  category: string;
  status: SharedRecordStatus;
  storedValue: string | null;
  freshValue: string | null;
  sourceUrl: string | null;
  /** 0..1 */
  confidence: number;
  flagReason: string | null;
}

export interface SharedRecordPage {
  items: SharedRecord[];
  total: number;
}

export interface SharedRecordReviewResult {
  recordId: string;
  status: 'Approved' | 'Rejected';
}

export class SharedRecordReviewError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'SharedRecordReviewError';
    this.status = status;
  }
}

const STATUS_MESSAGES: Record<number, string> = {
  403: 'Admin only: you need admin access to review shared records.',
  404: 'That record no longer exists.',
  409: 'That record was already reviewed.',
};

const LIST_NOT_FOUND = 'The shared record review queue is not available (HTTP 404).';

async function failure(response: Response, isList = false): Promise<SharedRecordReviewError> {
  const known = isList && response.status === 404 ? LIST_NOT_FOUND : STATUS_MESSAGES[response.status];
  if (known) return new SharedRecordReviewError(response.status, known);
  const body = (await response.json().catch(() => null)) as { error?: string; message?: string } | null;
  return new SharedRecordReviewError(
    response.status,
    body?.error || body?.message || `HTTP ${response.status}`,
  );
}

async function post(recordId: string, action: 'Approve' | 'Reject', body?: unknown): Promise<SharedRecordReviewResult> {
  const response = await authenticatedFetch(`${BASE}/${encodeURIComponent(recordId)}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw await failure(response);
  return (await response.json()) as SharedRecordReviewResult;
}

export async function listSharedRecords(
  status?: SharedRecordStatus,
  page = 1,
  pageSize = 25,
): Promise<SharedRecordPage> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (status) params.set('status', status);
  const response = await authenticatedFetch(`${BASE}?${params.toString()}`);
  if (!response.ok) throw await failure(response, true);
  return (await response.json()) as SharedRecordPage;
}

export function approveSharedRecord(recordId: string): Promise<SharedRecordReviewResult> {
  return post(recordId, 'Approve');
}

export function rejectSharedRecord(recordId: string, reason: string): Promise<SharedRecordReviewResult> {
  return post(recordId, 'Reject', { reason });
}
